import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, AlertCircle } from "lucide-react";
import Navbar from "@/components/Navbar";

const SCHEMA = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Why Indian professionals struggle with personal branding on LinkedIn",
    "description": "An honest look at why most Indian professionals never post consistently on LinkedIn — and what specifically holds them back. Plus how Lakshmi removes each barrier.",
    "url": "https://hilakshmi.com/blog/indian-professionals-linkedin-personal-branding",
    "author": { "@type": "Organization", "name": "Lakshmi", "url": "https://hilakshmi.com" },
    "publisher": { "@type": "Organization", "name": "Lakshmi", "url": "https://hilakshmi.com" },
    "datePublished": "2026-01-22",
    "keywords": "Indian professionals LinkedIn, personal branding India, LinkedIn post writer India, why Indians don't post LinkedIn",
    "image": "https://hilakshmi.com/image/og-cover.svg"
};

const BARRIERS = [
    {
        barrier: "\"I don't know what to write about\"",
        reality: "You have more content material than most Western LinkedIn gurus. 5 years of shipping features in Bengaluru, pivoting a startup in Chennai, or navigating enterprise sales in Delhi — that's years of material.",
        fix: "Lakshmi's Content Strategist extracts angles from a rough idea. You don't need to know the angle. Just drop the experience."
    },
    {
        barrier: "\"I don't have time\"",
        reality: "Writing a good LinkedIn post manually takes 45–90 minutes when you include the drafting, editing, second-guessing, and reformatting. Most people give up before they publish.",
        fix: "Lakshmi turns a rough idea into a publish-ready post in under 60 seconds. Including quality scoring."
    },
    {
        barrier: "\"I'm not a good writer\"",
        reality: "You don't need to be. LinkedIn rewards authenticity, not literary prose. The best Indian LinkedIn creators write exactly how they speak.",
        fix: "Lakshmi learns your voice from one post you're proud of. Every draft sounds like you — not a generic AI."
    },
    {
        barrier: "\"It feels awkward to self-promote\"",
        reality: "This is deeply Indian. We were raised not to brag. But sharing what you learned isn't bragging — it's teaching. The reframe: you're not promoting yourself, you're helping someone who is 5 years behind you.",
        fix: "Lakshmi frames your experience as insight, not self-promotion. The storytelling approach is inherently humble and useful."
    },
    {
        barrier: "\"I posted a few times and got no engagement\"",
        reality: "Early LinkedIn posts almost always underperform. It takes 60–90 days of consistent posting before the algorithm takes you seriously. Most people quit in week 3.",
        fix: "When posting takes 60 seconds instead of 60 minutes, the consistency barrier disappears. Lakshmi makes it easy to post every day."
    },
];

export default function BlogPost2() {
    useEffect(() => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.text = JSON.stringify(SCHEMA);
        document.head.appendChild(s);
        document.title = "Why Indian Professionals Struggle with LinkedIn Personal Branding — Lakshmi";
        const desc = document.querySelector('meta[name="description"]');
        if (desc) desc.setAttribute("content", "Why most Indian professionals never post consistently on LinkedIn — and what specifically holds them back. An honest look at 5 real barriers and how to overcome each one.");
        return () => s.remove();
    }, []);

    return (
        <div className="min-h-screen">
            <Navbar />
            <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-20">

                {/* Hero */}
                <header className="mb-12">
                    <div className="lk-label mb-3">Personal Branding · 6 min read</div>
                    <h1 className="font-heading text-3xl sm:text-5xl tracking-tight mb-6 leading-tight">
                        Why Indian professionals struggle with <span className="lk-gradient-text">personal branding</span> on LinkedIn
                    </h1>
                    <p className="text-[var(--lk-muted)] text-lg leading-relaxed">
                        It's not because they have nothing to say. It's because five specific things get in the way — and most of them are fixable in 60 seconds.
                    </p>
                </header>

                {/* Blog hero illustration */}
                <div className="mb-10 lk-border lk-surface rounded-2xl overflow-hidden">
                    <picture>
                        <source srcSet="/image/blog/personal-branding-india.webp" type="image/webp" />
                        <img src="/image/blog/personal-branding-india.png" alt="Indian professional personal branding"
                            className="w-full h-48 sm:h-64 object-cover" loading="lazy"
                            onError={e => { e.target.style.display = "none"; }} />
                    </picture>
                </div>

                {/* Character + intro block */}
                <div className="flex flex-col sm:flex-row items-center gap-6 lk-border lk-surface rounded-2xl p-6 mb-12">
                    <picture>
                        <source srcSet="/image/lakshmi_nobg.webp" type="image/webp" />
                        <img src="/image/lakshmi_nobg_opt.png" alt="Lakshmi AI"
                            className="w-28 sm:w-36 shrink-0" loading="lazy"
                            style={{ filter: "drop-shadow(0 0 20px rgba(124,58,237,0.4))" }} />
                    </picture>
                    <div>
                        <p className="text-sm leading-relaxed text-[var(--lk-muted)]">
                            India has 97 million LinkedIn users — the second largest audience in the world. Yet most Indian professionals have under 1,000 followers and post less than once a month. Something is blocking them. Let's name it.
                        </p>
                    </div>
                </div>

                {/* The 5 barriers */}
                <section className="mb-12">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-8">The 5 real barriers — and how to break each one</h2>
                    <div className="space-y-5">
                        {BARRIERS.map((b, i) => (
                            <motion.div key={i}
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.08 }}
                                viewport={{ once: true }}
                                className="lk-border lk-surface rounded-2xl p-6"
                            >
                                <div className="flex items-start gap-3 mb-3">
                                    <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                                    <div className="font-semibold text-base">{b.barrier}</div>
                                </div>
                                <p className="text-sm text-[var(--lk-muted)] leading-relaxed mb-3">{b.reality}</p>
                                <div className="border-t border-[var(--lk-border)] pt-3">
                                    <div className="text-xs font-semibold text-[var(--lk-primary)] uppercase tracking-wider mb-1">How Lakshmi fixes this</div>
                                    <p className="text-sm leading-relaxed">{b.fix}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </section>

                {/* India-specific context */}
                <section className="mb-12 lk-border rounded-2xl p-7"
                    style={{ background: "rgba(124,58,237,0.04)", borderColor: "rgba(124,58,237,0.3)" }}>
                    <h2 className="font-semibold text-lg mb-4">The Indian professional opportunity</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {[
                            { stat: "97M", label: "Indian LinkedIn users" },
                            { stat: "#2", label: "World's largest LinkedIn audience" },
                            { stat: "&lt;5%", label: "Post at least once a week" },
                            { stat: "5x", label: "More profile views for active posters" },
                            { stat: "3x", label: "More inbound opportunities" },
                            { stat: "Now", label: "Best time to start" },
                        ].map(s => (
                            <div key={s.stat} className="lk-border lk-surface rounded-xl p-4 text-center">
                                <div className="font-bold text-lg lk-gradient-text" dangerouslySetInnerHTML={{ __html: s.stat }} />
                                <div className="text-xs text-[var(--lk-muted)] mt-1">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* CTA */}
                <div className="text-center lk-border lk-surface rounded-2xl p-8">
                    <h3 className="font-heading text-2xl tracking-tight mb-3">Start posting in under 60 seconds — free</h3>
                    <p className="text-[var(--lk-muted)] text-sm mb-6">No credit card. No blank screen. Lakshmi turns your rough idea into a high-quality LinkedIn post in your own voice.</p>
                    <Link to="/signup" className="lk-btn-primary rounded-xl px-8 py-4 inline-flex items-center gap-2 text-sm">
                        Write my first post <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </article>
        </div>
    );
}
